import { z } from "zod";
import { EBillRateType, schemes } from "@zeal/common";
import {
  ApprovalAndDeliverySchema,
  DiscountsAndFeesSchema,
  NotesSchema,
  PaymentSchema,
} from "../createInvoiceTemplate/createInvoiceTemplate.schema";

export enum EInvoiceStatus {
  PAID = "paid",
  PENDING = "pending",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
  DRAFT = "draft",
  SENT = "sent",
  VIEWED = "viewed",
  UNCOLLECTIBLE = "uncollectible",
  APPROVED = "approved",
}

export const ShiftEarningsSchema = z.object({
  hours: z.number(),
  wage: z.number(),
  bill_rate: z.number().optional(),
  bill_rate_type: z.nativeEnum(EBillRateType).optional(),
  amount: z.number().optional(),
});

export const ShiftSchema = z.object({
  shiftID: z.string(),
  employeeID: z.string(),
  time: z.string(),
  first_name: z.string(),
  middle_initial: z.string().optional(),
  last_name: z.string(),
  hourly: ShiftEarningsSchema.optional(),
  overtime: ShiftEarningsSchema.optional(),
  doubletime: ShiftEarningsSchema.optional(),
  pto: ShiftEarningsSchema.optional(),
  vacation: ShiftEarningsSchema.optional(),
  sick: ShiftEarningsSchema.optional(),
  holiday: ShiftEarningsSchema.optional(),
  piecework: ShiftEarningsSchema.optional(),
  flat: ShiftEarningsSchema.pick({
    hours: true,
    amount: true,
  }).optional(),
  correction: ShiftEarningsSchema.pick({
    hours: true,
    amount: true,
    bill_rate: true,
  }).optional(),
  meal_break: z
    .object({
      units: z.number(),
      amount_per_unit: z.number(),
    })
    .optional(),
  rest_break: z
    .object({
      units: z.number(),
      amount_per_unit: z.number(),
    })
    .optional(),
  workLocationID: z.string().optional(),
  customerAccountID: z.string(),
});

export const invoiceContractorPaymentSchema = z.object({
  _id: z.string(),
  pay_date: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  amount: z.number(),
  workLocationID: z.string().optional(),
  customerAccountID: z.string(),
});

export const createInvoiceRequestSchema = z.object({
  invoiceTemplateID: z.string(),
  customerAccountID: z.string(),
  status: z.nativeEnum(EInvoiceStatus).optional(),
  invoice_number: z.string(),
  items: z.array(
    z.object({
      id: z.string(),
      description: z.string(),
      quantity: z.number().optional(),
      price: z.number().optional(),
      is_removable: z.boolean().optional(),
    })
  ),
  employee_items: z.array(ShiftSchema),
  contractor_items: z.array(invoiceContractorPaymentSchema),
  discounts_and_fees: DiscountsAndFeesSchema,
  payment: PaymentSchema,
  notes: NotesSchema,
  approval_and_delivery: ApprovalAndDeliverySchema,
  invoice_date: z.string(),
  due_date: z.string(),
  amount: z.number(),
});

export type TInvoiceContractorPayment = z.infer<
  typeof invoiceContractorPaymentSchema
>;

export const createInvoiceResponseSchema = createInvoiceRequestSchema.extend({
  invoiceID: schemes.uuid(),
  companyID: schemes.companyID(),
  status: z.nativeEnum(EInvoiceStatus),
});

export const createInvoicesRequestSchema = z.object({
  invoices: z.array(createInvoiceRequestSchema),
});
export const createInvoicesResponseSchema = z.object({
  invoices: z.array(createInvoiceResponseSchema),
});

export type TCreateInvoiceRequest = z.infer<typeof createInvoiceRequestSchema>;
export type TCreateInvoicesRequest = z.infer<
  typeof createInvoicesRequestSchema
>;
export type TCreateInvoiceResponse = z.infer<
  typeof createInvoiceResponseSchema
>;
export type TCreateInvoicesResponse = z.infer<
  typeof createInvoicesResponseSchema
>;
